import { PIAPI } from '@core/base';
import { CreateTaskRequest, TaskResponse } from '@core/types';
import { Kling } from './types';

export class KlingEffectsAPI extends PIAPI {
    private config: Kling.Config;

    constructor(apiKey: string, config: Kling.Config = {}, baseUrl?: string) {
        super(apiKey, baseUrl);
        this.config = config;
    }

    // Effects 配置信息 - 定义每个 effect 的可用模式和是否需要 prompt
    private static EFFECTS_CONFIG: Record<Kling.EffectName, {
        stdMode: boolean;
        proMode: boolean;
        requiresPrompt: boolean;
    }> = {
        squish: { stdMode: true, proMode: false, requiresPrompt: false },
        expansion: { stdMode: true, proMode: false, requiresPrompt: false },
        jellycat_oversea: { stdMode: true, proMode: false, requiresPrompt: false },
        spinoff: { stdMode: false, proMode: true, requiresPrompt: false },
        rocket: { stdMode: true, proMode: true, requiresPrompt: true },
        hearting: { stdMode: true, proMode: true, requiresPrompt: true },
        fighting: { stdMode: true, proMode: true, requiresPrompt: true },
        kissing: { stdMode: true, proMode: true, requiresPrompt: true },
        hugging: { stdMode: true, proMode: true, requiresPrompt: true },
        figure: { stdMode: true, proMode: false, requiresPrompt: false },
    };

    private createEffectsRequest(params: Kling.EffectParams): CreateTaskRequest {
        // 验证必需参数
        if (!params.image_url) {
            throw new Error("image_url is required");
        }

        if (!params.effect) {
            throw new Error("effect is required");
        }

        // 验证 effect 是否有效
        const effectConfig = KlingEffectsAPI.EFFECTS_CONFIG[params.effect];
        if (!effectConfig) {
            throw new Error(`Invalid effect: ${params.effect}`);
        }

        const isProfessionalMode = params.professional_mode || false;

        // 验证模式兼容性
        if (isProfessionalMode && !effectConfig.proMode) {
            throw new Error(`Effect "${params.effect}" is not available in professional mode`);
        }

        if (!isProfessionalMode && !effectConfig.stdMode) {
            throw new Error(`Effect "${params.effect}" is only available in professional mode`);
        }

        // 验证 prompt 要求
        if (effectConfig.requiresPrompt && !params.prompt) {
            throw new Error(`Effect "${params.effect}" requires a prompt`);
        }

        const request: CreateTaskRequest = {
            model: "kling",
            task_type: "effects",
            input: {
                image_url: params.image_url,
                effect: params.effect,
                ...(params.prompt && { prompt: params.prompt }),
                ...(params.professional_mode && { professional_mode: params.professional_mode }),
            },
        };

        if (this.config.webhookUrl && this.config.webhookSecret) {
            request.config = {
                webhook_config: {
                    endpoint: this.config.webhookUrl,
                    secret: this.config.webhookSecret,
                },
            };
        }

        return request;
    }

    async applyEffect(params: Kling.EffectParams): Promise<TaskResponse<Kling.EffectOutput>> {
        const request = this.createEffectsRequest(params);
        return this.createTask<Kling.EffectOutput>(request);
    }

    async applyEffectAndWait(params: Kling.EffectParams, options?: {
        maxAttempts?: number;
        interval?: number;
    }): Promise<TaskResponse<Kling.EffectOutput>> {
        const request = this.createEffectsRequest(params);
        return this.createTaskAndWait<Kling.EffectOutput>(request, options);
    }

    // 获取特定 effect 任务状态
    async getEffectTask(taskId: string): Promise<TaskResponse<Kling.EffectOutput>> {
        return this.getTask<Kling.EffectOutput>(taskId);
    }

    // 静态方法：获取指定 effect 的配置信息
    static getEffectConfig(effect: Kling.EffectName) {
        return KlingEffectsAPI.EFFECTS_CONFIG[effect];
    }

    // 静态方法：获取所有可用的 effects
    static getAvailableEffects(): Kling.EffectName[] {
        return Object.keys(KlingEffectsAPI.EFFECTS_CONFIG) as Kling.EffectName[];
    }

    // 静态方法：根据模式筛选可用的 effects
    static getEffectsByMode(professionalMode: boolean = false): Kling.EffectName[] {
        return Object.entries(KlingEffectsAPI.EFFECTS_CONFIG)
            .filter(([_, config]) => professionalMode ? config.proMode : config.stdMode)
            .map(([effect]) => effect as Kling.EffectName);
    }
} 